/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import { useTheme } from "@mui/material";
import { Link } from "react-router-dom";
import { Box } from "../Box";
import { Paper } from "../Paper";
import { Typography } from "../Typography";
import { CalendarEventProps } from "./ICalendarEventProps";

const CalendarEvent = ({
  time,
  mins,
  title,
  location,
  color,
  isLink = false,
  ...rest
}: CalendarEventProps) => {
  const theme = useTheme();

  const content = (
    <Paper
      sx={{
        ...rest.sxStyles,
        backgroundColor: color,
        borderLeft: `0.25rem solid ${theme.palette.primary.main}`,
        elevation: 0, 
        boxShadow: "none", 
        padding: theme.spacing(1), 
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start", 
          gap: "1rem",
        }}
      >
        {/* Time and Duration */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column", 
            justifyContent: "center", 
          }}
        >
          <Typography variant="body2" sx={{color: '#000'}}>
            {time}
          </Typography>
          <Typography variant="body2" sx={{color: '#000'}}>
            {mins} min
          </Typography>
        </Box>

        {/* Event Name and Location */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column", 
            justifyContent: "center",
            alignItems: "flex-start", 
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold", color: '#000' }}>
            {title} 
          </Typography>
          {location && (
            <Typography variant="body2" sx={{ color: '#0000008a', fontSize: theme.typography.body2.fontSize }}>
              {location}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );

  // If isLink is true, wrap the entire Paper with a Link component to make it clickable
  if (isLink) {
    return <Link to="/link-target-url">{content}</Link>; // Replace with actual link target
  }

  return content;
};

export default CalendarEvent;
